// src/Views/FavoritesView.js
import React, { useState, useContext, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { FavoritesContext } from '../contexts/FavoritesContext';
import { MAX_CONTENT_WIDTH, BORDER_RADIUS, SPACING_COMPACT, SPACING_BUTTON, SPACING_CARD, SPACING_GROUP, SPACING_SECTION, SPACING_HERO, SPACING_PAGE } from '../constants/layout';
import {
  FONT_TITLE,
  FONT_TITLE2,
  FONT_TITLE2_MOBILE,
  FONT_FOOTNOTE,
} from '../constants/typography';

function FavoritesView() {
  const { favorites, removeFavorite } = useContext(FavoritesContext);
  const [allProducts, setAllProducts] = useState([]);
  const [allImages, setAllImages] = useState([]);

  // Fetch all products for displaying favorites
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'products'), (snapshot) => {
      const products = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllProducts(products);
    });

    return () => unsubscribe();
  }, []);

  // Fetch all images for product thumbnails
  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, 'imageItems'), (snapshot) => {
      const images = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setAllImages(images);
    });

    return () => unsubscribe();
  }, []);

  // Helper to get product by ID
  const getProductById = (productID) => {
    return allProducts.find(p => p.id === productID);
  };

  // Helper to get product image
  const getProductImage = (product) => {
    if (!product) return null;

    // Try to find image by imageItemID
    if (product.productImage?.imageItemID) {
      const image = allImages.find(img => img.id === product.productImage.imageItemID);
      if (image) return image.imageURL;
    }

    // Try to find image by productID
    const image = allImages.find(img => img.productID === product.id);
    return image?.imageURL || null;
  };

  if (favorites.length === 0) {
    return (
      <div className="favorites-container">
        <div className="empty-favorites">
          <div className="empty-icon">
            <span className="material-icons">favorite_border</span>
          </div>
          <h2 className="empty-title">No Favorites Yet</h2>
          <p className="empty-description">
            Start adding products to your favorites to see them here.
          </p>
          <Link to="/" className="continue-shopping-button">
            Browse Products
          </Link>
        </div>

        <style jsx>{`
          .favorites-container {
            max-width: ${MAX_CONTENT_WIDTH};
            margin: 0 auto;
            padding: 0 ${SPACING_CARD};
            min-height: calc(100vh - 200px);
            display: flex;
            align-items: center;
            justify-content: center;
          }

          .empty-favorites {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            padding: ${SPACING_GROUP};
            text-align: center;
          }

          .empty-icon {
            margin-bottom: ${SPACING_GROUP};
            opacity: 0.5;
          }

          .empty-icon .material-icons {
            font-size: 80px;
            color: var(--color-accent);
          }

          .empty-title {
            font-size: ${FONT_TITLE};
            font-weight: 700;
            margin: 0 0 ${SPACING_BUTTON} 0;
            color: var(--color-text-primary);
          }

          .empty-description {
            font-size: 1rem;
            color: var(--color-text-secondary);
            margin: 0 0 ${SPACING_SECTION} 0;
          }

          .continue-shopping-button {
            display: inline-block;
            padding: ${SPACING_BUTTON} ${SPACING_GROUP};
            background-color: var(--color-accent);
            color: var(--color-accent-text);
            text-decoration: none;
            border-radius: ${BORDER_RADIUS};
            font-weight: 600;
            transition: background-color 0.2s ease;
          }

          .continue-shopping-button:hover {
            background-color: var(--color-accent-hover);
          }

          @media (max-width: 600px) {
            .section-title {
              font-size: ${FONT_TITLE2_MOBILE};
            }
          }
        `}</style>
      </div>
    );
  }

  return (
    <div className="favorites-container">
      <div className="favorites-header">
        <h1 className="section-title">Favorites</h1>
      </div>

      <div className="favorites-grid">
        {favorites.map(favorite => {
          const product = getProductById(favorite.productID);
          if (!product) return null;

          const imageURL = getProductImage(product);

          return (
            <div key={favorite.id} className="favorite-item">
              <Link to={`/product/${product.id}`} className="favorite-link">
                {imageURL ? (
                  <div className="favorite-image-container">
                    <img
                      src={imageURL}
                      alt={product.productName}
                      className="favorite-image"
                    />
                  </div>
                ) : (
                  <div className="favorite-placeholder">
                    <span className="material-icons">photo</span>
                  </div>
                )}
                <div className="favorite-info">
                  <p className="favorite-name">{product.productName}</p>
                </div>
              </Link>
              <button
                onClick={() => removeFavorite(product.id)}
                className="remove-favorite-button"
                aria-label="Remove from favorites"
              >
                <span className="material-icons">close</span>
              </button>
            </div>
          );
        })}
      </div>

      <style jsx>{`
        .favorites-container {
          max-width: ${MAX_CONTENT_WIDTH};
          margin: 0 auto;
          padding: ${SPACING_HERO} ${SPACING_CARD} ${SPACING_PAGE};
        }

        .favorites-header {
          margin-bottom: ${SPACING_SECTION};
        }

        .section-title {
          font-size: ${FONT_TITLE2};
          font-family: var(--font-family-sans);
          font-weight: 800;
          margin: 0;
          text-transform: uppercase;
          color: var(--color-text-primary);
        }

        .favorites-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
          gap: ${SPACING_CARD};
        }

        .favorite-item {
          position: relative;
          background-color: var(--color-card-background);
          border: 1px solid var(--color-border);
          border-radius: ${BORDER_RADIUS};
          overflow: hidden;
        }

        .favorite-link {
          display: block;
          text-decoration: none;
          color: inherit;
        }

        .favorite-image-container {
          width: 100%;
          aspect-ratio: 1;
          overflow: hidden;
          background-color: var(--color-surface-muted);
        }

        .favorite-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .favorite-item:hover .favorite-image {
          transform: scale(1.05);
        }

        .favorite-placeholder {
          width: 100%;
          aspect-ratio: 1;
          display: flex;
          align-items: center;
          justify-content: center;
          background-color: var(--color-surface-muted);
        }

        .favorite-placeholder .material-icons {
          font-size: 48px;
          color: var(--color-text-secondary);
          opacity: 0.5;
        }

        .favorite-info {
          padding: ${SPACING_BUTTON};
        }

        .favorite-name {
          font-size: ${FONT_FOOTNOTE};
          font-weight: 600;
          margin: 0;
          color: var(--color-text-primary);
          display: -webkit-box;
          -webkit-line-clamp: 2;
          -webkit-box-orient: vertical;
          overflow: hidden;
        }

        .remove-favorite-button {
          position: absolute;
          top: ${SPACING_COMPACT};
          right: ${SPACING_COMPACT};
          width: 32px;
          height: 32px;
          border: none;
          background-color: rgba(255, 255, 255, 0.9);
          border-radius: 50%;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          opacity: 0;
          transition: all 0.2s ease;
        }

        .favorite-item:hover .remove-favorite-button {
          opacity: 1;
        }

        .remove-favorite-button:hover {
          background-color: rgba(239, 68, 68, 0.9);
          color: white;
          transform: scale(1.1);
        }

        .remove-favorite-button .material-icons {
          font-size: 20px;
        }

        @media (max-width: 600px) {
          .section-title {
            font-size: ${FONT_TITLE2_MOBILE};
          }

          .favorites-grid {
            grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
          }

          .remove-favorite-button {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
}

export default FavoritesView;
